/**
 * Evaluation Detail page — shows scores, full feedback, and revision flow.
 *
 * Polls the evaluation status until it transitions to 'completed' or 'failed'.
 * Then renders:
 * - 6-dimension score bars
 * - AI-powered improvement suggestions for weak dimensions (≤3)
 * - Full evaluation markdown
 * - PDF download button
 * - "Revise & Re-Evaluate" inline editor
 *
 * Revision flow:
 * 1. User clicks "Revise & Re-Evaluate"
 * 2. Editor opens pre-populated with the evaluated answer text
 * 3. User edits their answer based on feedback
 * 4. Submit creates a new AnswerVersion + Evaluation and navigates to it
 */

import React, { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import QuizIcon from "@mui/icons-material/Quiz";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import SendIcon from "@mui/icons-material/Send";
import ShareIcon from "@mui/icons-material/Share";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  getEvaluation,
  getEvaluationPdfUrl,
  createVersion,
  createEvaluation,
  shareEvaluation,
  revokeShare,
  createTemplate,
  getEvaluationSuggestions,
  SuggestionItem,
} from "../api/client";
import AgenticEvaluationView from "../components/AgenticEvaluationView";
import AppBreadcrumbs from "../components/AppBreadcrumbs";
import ScoreBar from "../components/ScoreBar";
import SimpleMarkdown from "../components/SimpleMarkdown";
import PageLoader from "../components/PageLoader";
import UpgradePrompt, { isTierLimitError } from "../components/UpgradePrompt";

export default function EvaluationDetail() {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Revision state
  const [revising, setRevising] = useState(false);
  const [revisedText, setRevisedText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [revisionError, setRevisionError] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [tierLimitInfo, setTierLimitInfo] = useState<{
    currentUsage?: number;
    limit?: number;
  }>({});

  // Share state
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Save as template state
  const [savedAsTemplate, setSavedAsTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Inline suggestions state
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [suggestionsMessage, setSuggestionsMessage] = useState<string | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);

  // Bound the polling: stop after this long without reaching a terminal state.
  // The backend stale-row reaper usually flips a stuck eval to "failed" before
  // this fires (~5 min server-side); this is the client-side backstop so the
  // spinner can never run forever even if the reaper is slow or the read fails.
  const POLL_TIMEOUT_MS = 120_000;
  const pollStartRef = useRef<number>(Date.now());
  const [pollTimedOut, setPollTimedOut] = useState(false);

  const {
    data: evaluation,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["evaluation", id],
    queryFn: () => getEvaluation(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      // Stop polling once the eval reaches a terminal state.
      if (data?.status === "completed" || data?.status === "failed") {
        return false;
      }
      // Client backstop: stop polling after the elapsed cap and surface a
      // "taking too long" state with a manual retry.
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        setPollTimedOut(true);
        return false;
      }
      return 2000;
    },
  });

  // Manual retry from the timeout backstop: reset the clock and resume polling.
  const handleRetryPolling = () => {
    pollStartRef.current = Date.now();
    setPollTimedOut(false);
    refetch();
  };

  // Open revision editor with the current answer pre-populated
  const handleStartRevise = () => {
    if (evaluation?.answer_text) {
      setRevisedText(evaluation.answer_text);
    }
    setRevisionError(null);
    setRevising(true);
  };

  // Submit revised answer: create new version → create evaluation → navigate
  const handleSubmitRevision = async () => {
    if (!evaluation?.answer_id) {
      setRevisionError(t("EvaluationDetail.missingAnswerContextError"));
      return;
    }
    if (revisedText.trim().split(/\s+/).length < 10) {
      setRevisionError(t("EvaluationDetail.revisionTooShortError"));
      return;
    }
    if (revisedText.trim() === evaluation.answer_text?.trim()) {
      setRevisionError(t("EvaluationDetail.revisionIdenticalError"));
      return;
    }

    setSubmitting(true);
    setRevisionError(null);

    try {
      // 1. Create new version
      const newVersion = await createVersion(evaluation.answer_id, {
        answer_text: revisedText.trim(),
      });

      // 2. Kick off evaluation on the new version
      const newEval = await createEvaluation(newVersion.id);

      // 3. Navigate to the new evaluation (will show polling → results)
      navigate(`/evaluations/${newEval.id}`, { replace: false });
    } catch (err: any) {
      const tierCheck = isTierLimitError(err);
      if (tierCheck.isLimit) {
        setTierLimitInfo({
          currentUsage: tierCheck.currentUsage,
          limit: tierCheck.limit,
        });
        setShowUpgrade(true);
        return;
      }
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || err?.message || t("EvaluationDetail.revisionSubmitFailedError");
      setRevisionError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Share or copy existing share link
  const handleShare = async () => {
    if (!evaluation) return;

    // If already shared, just copy the URL
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }

    setSharing(true);
    try {
      const result = await shareEvaluation(evaluation.id);
      setShareUrl(result.share_url);
      await navigator.clipboard.writeText(result.share_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      refetch(); // refresh to show share_token in evaluation data
    } catch {
      // Silently fail — the button will remain available to retry
    } finally {
      setSharing(false);
    }
  };

  // Revoke sharing
  const handleRevokeShare = async () => {
    if (!evaluation) return;
    try {
      await revokeShare(evaluation.id);
      setShareUrl(null);
      refetch();
    } catch {
      // Silently fail
    }
  };

  // Save as template
  const handleSaveAsTemplate = async () => {
    if (!evaluation?.answer_text) return;
    setSavingTemplate(true);
    try {
      await createTemplate({
        name: t("EvaluationDetail.templateNameFromEvaluation", {
          date: new Date().toLocaleDateString(),
        }),
        template_text: evaluation.answer_text,
      });
      setSavedAsTemplate(true);
    } catch {
      // Silently fail — button stays available for retry
    } finally {
      setSavingTemplate(false);
    }
  };

  // Load AI-powered suggestions for weak dimensions
  const handleLoadSuggestions = async () => {
    if (!evaluation || suggestionsLoaded) return;
    setLoadingSuggestions(true);
    try {
      const result = await getEvaluationSuggestions(evaluation.id);
      setSuggestions(result.suggestions);
      setSuggestionsMessage(result.message || null);
      setSuggestionsLoaded(true);
    } catch {
      setSuggestionsMessage(t("EvaluationDetail.loadSuggestionsFailedError"));
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Check if any dimension scored ≤3 (suggestions are relevant)
  const hasWeakDimensions =
    evaluation?.status === "completed" &&
    [
      evaluation.situation_score,
      evaluation.task_score,
      evaluation.action_score,
      evaluation.result_score,
      evaluation.engagement_score,
      evaluation.overall_score,
    ].some((score) => score !== null && score <= 3);

  if (isLoading) {
    return <PageLoader message={t("EvaluationDetail.loadingMessage")} />;
  }

  if (error) {
    return (
      <Alert severity="error">
        {t("EvaluationDetail.loadFailedError")}
      </Alert>
    );
  }

  if (!evaluation) {
    return <Alert severity="warning">{t("EvaluationDetail.notFoundMessage")}</Alert>;
  }

  // --- Polling timeout backstop (non-terminal eval that's taking too long) ---
  if (
    pollTimedOut &&
    (evaluation.status === "queued" || evaluation.status === "analyzing")
  ) {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto" }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={500}>
            {t("EvaluationDetail.pollTimeoutTitle")}
          </Typography>
          <Typography variant="body2">
            {t("EvaluationDetail.pollTimeoutDescription")}
          </Typography>
        </Alert>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={handleRetryPolling}>
            {t("EvaluationDetail.refreshButton")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/evaluate")}
          >
            {t("EvaluationDetail.tryAgainButton")}
          </Button>
        </Stack>
      </Box>
    );
  }

  // --- In-progress states ---
  if (evaluation.status === "queued" || evaluation.status === "analyzing") {
    return (
      <Box sx={{ textAlign: "center", py: 8, maxWidth: 500, mx: "auto" }}>
        <CircularProgress size={64} thickness={3} />
        <Typography variant="h5" sx={{ mt: 3, mb: 1 }}>
          {evaluation.status === "queued"
            ? t("EvaluationDetail.queuedTitle")
            : t("EvaluationDetail.analyzingTitle")}
        </Typography>
        <Typography color="text.secondary">
          {evaluation.status === "queued"
            ? t("EvaluationDetail.queuedDescription")
            : evaluation.evaluation_type === "agentic"
            ? t("EvaluationDetail.analyzingAgenticDescription")
            : t("EvaluationDetail.analyzingStarDescription")}
        </Typography>
        <Chip
          label={evaluation.status}
          color="primary"
          variant="outlined"
          sx={{ mt: 2 }}
        />
      </Box>
    );
  }

  // --- Failed state ---
  if (evaluation.status === "failed") {
    return (
      <Box sx={{ maxWidth: 600, mx: "auto" }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={500}>
            {t("EvaluationDetail.failedTitle")}
          </Typography>
          <Typography variant="body2">
            {evaluation.error_message || t("EvaluationDetail.unexpectedErrorMessage")}
          </Typography>
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/evaluate")}
        >
          {t("EvaluationDetail.tryAgainButton")}
        </Button>
      </Box>
    );
  }

  // Word count for revision
  const wordCount = revisedText.trim().split(/\s+/).filter(Boolean).length;

  // --- Completed state ---
  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <AppBreadcrumbs
        crumbs={[
          { label: t("EvaluationDetail.breadcrumbDashboard"), path: "/" },
          {
            label: evaluation.evaluation_type === "agentic"
              ? t("EvaluationDetail.agenticEvaluationTitle")
              : t("EvaluationDetail.evaluationResultsTitle"),
          },
        ]}
      />

      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontSize: { xs: "1.4rem", sm: "2.125rem" } }}
          >
            {evaluation.evaluation_type === "agentic"
              ? t("EvaluationDetail.agenticEvaluationTitle")
              : t("EvaluationDetail.evaluationResultsTitle")}
            {evaluation.version_number && (
              <Chip
                label={`v${evaluation.version_number}`}
                size="small"
                sx={{ ml: 1, verticalAlign: "middle" }}
              />
            )}
            {evaluation.evaluation_type === "agentic" && (
              <Chip
                label={t("EvaluationDetail.agenticChipLabel")}
                size="small"
                color="primary"
                sx={{ ml: 1, verticalAlign: "middle" }}
              />
            )}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={evaluation.model_used || t("EvaluationDetail.modelFallbackLabel")} size="small" />
            {evaluation.processing_seconds && (
              <Chip
                label={`${evaluation.processing_seconds}s`}
                size="small"
                variant="outlined"
              />
            )}
            {evaluation.input_tokens && evaluation.output_tokens && (
              <Chip
                label={t("EvaluationDetail.tokensChipLabel", {
                  tokens: (
                    evaluation.input_tokens + evaluation.output_tokens
                  ).toLocaleString(),
                })}
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          flexWrap="wrap"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <Button
            variant="contained"
            color="secondary"
            startIcon={<EditIcon />}
            onClick={handleStartRevise}
            disabled={!evaluation.answer_id || revising}
            fullWidth={false}
          >
            {t("EvaluationDetail.reviseButton")}
          </Button>
          {evaluation.answer_id && (
            <Button
              variant="outlined"
              startIcon={<CompareArrowsIcon />}
              onClick={() =>
                navigate(`/answers/${evaluation.answer_id}/compare`)
              }
            >
              {t("EvaluationDetail.compareVersionsButton")}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            href={getEvaluationPdfUrl(evaluation.id)}
            target="_blank"
          >
            {t("EvaluationDetail.pdfButton")}
          </Button>
          <Button
            variant={shareUrl || evaluation.share_token ? "contained" : "outlined"}
            color={copied ? "success" : "primary"}
            startIcon={
              sharing ? (
                <CircularProgress size={18} color="inherit" />
              ) : copied ? (
                <CheckIcon />
              ) : (
                <ShareIcon />
              )
            }
            onClick={handleShare}
            disabled={sharing}
          >
            {copied
              ? t("EvaluationDetail.copiedButton")
              : shareUrl || evaluation.share_token
              ? t("EvaluationDetail.copyLinkButton")
              : t("EvaluationDetail.shareButton")}
          </Button>
          {evaluation.answer_text && (
            <Button
              variant="outlined"
              startIcon={
                savingTemplate ? (
                  <CircularProgress size={18} color="inherit" />
                ) : savedAsTemplate ? (
                  <CheckIcon />
                ) : (
                  <BookmarkAddIcon />
                )
              }
              onClick={handleSaveAsTemplate}
              disabled={savingTemplate || savedAsTemplate}
              color={savedAsTemplate ? "success" : "primary"}
            >
              {savedAsTemplate ? t("EvaluationDetail.savedButton") : t("EvaluationDetail.saveAsTemplateButton")}
            </Button>
          )}
          {(shareUrl || evaluation.share_token) && (
            <Button
              variant="text"
              size="small"
              startIcon={<LinkOffIcon />}
              onClick={handleRevokeShare}
              color="error"
              sx={{ minWidth: "auto" }}
            >
              {t("EvaluationDetail.revokeButton")}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Question context — links back to source question */}
      {evaluation.question_text && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <QuizIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {t("EvaluationDetail.questionLabel")}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {evaluation.question_text}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {evaluation.question_id && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        navigate("/questions", {
                          state: { highlightId: evaluation.question_id },
                        })
                      }
                    >
                      {t("EvaluationDetail.viewInQuestionBankButton")}
                    </Button>
                  )}
                  {evaluation.question_id && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        navigate("/mock", {
                          state: {
                            questionId: evaluation.question_id,
                            questionText: evaluation.question_text,
                          },
                        })
                      }
                    >
                      {t("EvaluationDetail.practiceAgainButton")}
                    </Button>
                  )}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Revision Editor — collapsible panel */}
      <Collapse in={revising}>
        <Card
          sx={{
            mb: 3,
            border: 2,
            borderColor: "secondary.main",
            bgcolor: "secondary.50",
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6" color="secondary.dark">
                {t("EvaluationDetail.reviseYourAnswerTitle")}
              </Typography>
              <Button
                size="small"
                startIcon={<CloseIcon />}
                onClick={() => setRevising(false)}
                color="inherit"
              >
                {t("EvaluationDetail.cancelButton")}
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("EvaluationDetail.reviseInstructions")}
            </Typography>

            <TextField
              multiline
              minRows={8}
              maxRows={20}
              fullWidth
              value={revisedText}
              onChange={(e) => setRevisedText(e.target.value)}
              placeholder={t("EvaluationDetail.revisePlaceholder")}
              sx={{
                mb: 1,
                "& .MuiOutlinedInput-root": { bgcolor: "white" },
              }}
            />

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="caption" color="text.secondary">
                {t("EvaluationDetail.wordCountLabel", { count: wordCount })}
                {wordCount < 100 && t("EvaluationDetail.wordCountAimHint")}
              </Typography>
              <Stack direction="row" spacing={1}>
                {revisionError && (
                  <Alert severity="error" sx={{ py: 0 }}>
                    {revisionError}
                  </Alert>
                )}
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={
                    submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  onClick={handleSubmitRevision}
                  disabled={submitting || wordCount < 10}
                >
                  {submitting ? t("EvaluationDetail.submittingButton") : t("EvaluationDetail.submitRevisionButton")}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Collapse>

      {/* Agentic evaluations use a completely different layout */}
      {evaluation.evaluation_type === "agentic" ? (
        <AgenticEvaluationView evaluation={evaluation} />
      ) : (
      <Grid container spacing={3}>
        {/* Score Panel */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: "sticky", top: 80 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("EvaluationDetail.dimensionScoresTitle")}
              </Typography>

              {/* Average score highlight */}
              {evaluation.average_score !== null && (
                <Paper
                  elevation={0}
                  sx={{
                    textAlign: "center",
                    py: 2,
                    mb: 2,
                    bgcolor:
                      evaluation.average_score >= 4
                        ? "secondary.light"
                        : evaluation.average_score >= 3
                        ? "warning.light"
                        : "error.light",
                    borderRadius: 2,
                    // Fixed dark ink, not `text.primary`: the severity tints above are
                    // hardcoded light in BOTH themes, so white (or a theme-flipping ink)
                    // fails WCAG contrast — esp. on warning.light ([hex], ~1.3:1).
                    // [hex] on the lightest tint is ~7.3:1 (AA for the small caption too).
                    color: theme.palette.text.primary,
                  }}
                >
                  <Typography variant="h3" fontWeight={500}>
                    {evaluation.average_score}
                  </Typography>
                  <Typography variant="caption">
                    {t("EvaluationDetail.averageScoreCaption")}
                  </Typography>
                </Paper>
              )}

              <ScoreBar
                label={t("EvaluationDetail.situationScoreLabel")}
                score={evaluation.situation_score}
              />
              <ScoreBar label={t("EvaluationDetail.taskScoreLabel")} score={evaluation.task_score} />
              <ScoreBar label={t("EvaluationDetail.actionScoreLabel")} score={evaluation.action_score} />
              <ScoreBar label={t("EvaluationDetail.resultScoreLabel")} score={evaluation.result_score} />
              <ScoreBar
                label={t("EvaluationDetail.engagementScoreLabel")}
                score={evaluation.engagement_score}
              />
              <Divider sx={{ my: 1 }} />
              <ScoreBar label={t("EvaluationDetail.overallScoreLabel")} score={evaluation.overall_score} />
            </CardContent>
          </Card>

          {/* Company Alignment */}
          {evaluation.company_alignment && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("EvaluationDetail.companyAlignmentTitle")}
                </Typography>
                {evaluation.company_alignment.aligned_principles?.length >
                  0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      color="secondary.main"
                      fontWeight={500}
                    >
                      {t("EvaluationDetail.alignedLabel")}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {evaluation.company_alignment.aligned_principles.map(
                        (p: string) => (
                          <Chip
                            key={p}
                            label={p}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )
                      )}
                    </Stack>
                  </Box>
                )}
                {evaluation.company_alignment.reinforce_areas?.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="warning.main"
                      fontWeight={500}
                    >
                      {t("EvaluationDetail.reinforceLabel")}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {evaluation.company_alignment.reinforce_areas.map(
                        (a: string) => (
                          <Chip
                            key={a}
                            label={a}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )
                      )}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* AI Improvement Suggestions */}
          {hasWeakDimensions && (
            <Card
              sx={{
                mt: 2,
                border: 1,
                borderColor: "info.light",
                bgcolor: "info.50",
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: suggestionsLoaded && suggestions.length > 0 ? 1 : 0 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <AutoFixHighIcon color="info" />
                    <Typography variant="h6" color="info.dark">
                      {t("EvaluationDetail.howToImproveTitle")}
                    </Typography>
                  </Stack>
                  {!suggestionsLoaded && (
                    <Button
                      size="small"
                      variant="contained"
                      color="info"
                      startIcon={
                        loadingSuggestions ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <LightbulbIcon />
                        )
                      }
                      onClick={handleLoadSuggestions}
                      disabled={loadingSuggestions}
                    >
                      {loadingSuggestions ? t("EvaluationDetail.thinkingButton") : t("EvaluationDetail.getAiTipsButton")}
                    </Button>
                  )}
                  {suggestionsLoaded && suggestions.length > 0 && (
                    <Button
                      size="small"
                      onClick={() => setSuggestionsExpanded(!suggestionsExpanded)}
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: suggestionsExpanded
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                        />
                      }
                    >
                      {suggestionsExpanded ? t("EvaluationDetail.hideButton") : t("EvaluationDetail.showButton")}
                    </Button>
                  )}
                </Stack>

                {!suggestionsLoaded && !loadingSuggestions && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t("EvaluationDetail.suggestionsPromptText")}
                  </Typography>
                )}

                {suggestionsMessage && suggestions.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {suggestionsMessage}
                  </Typography>
                )}

                <Collapse in={suggestionsExpanded}>
                  <Stack spacing={1.5} sx={{ mt: suggestions.length > 0 ? 1 : 0 }}>
                    {suggestions.map((s, i) => (
                      <Paper
                        key={i}
                        elevation={0}
                        sx={{ p: 1.5, bgcolor: "background.paper", borderRadius: 1 }}
                      >
                        <Chip
                          label={s.section.charAt(0).toUpperCase() + s.section.slice(1)}
                          size="small"
                          color="info"
                          sx={{ mb: 0.5, fontWeight: 500 }}
                        />
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {s.suggestion}
                        </Typography>
                        {s.example && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontStyle: "italic",
                              pl: 1.5,
                              borderLeft: 2,
                              borderColor: "info.light",
                            }}
                          >
                            {s.example}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Collapse>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Full Evaluation */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("EvaluationDetail.fullEvaluationTitle")}
              </Typography>
              {evaluation.evaluation_markdown ? (
                <SimpleMarkdown content={evaluation.evaluation_markdown} />
              ) : (
                <Typography color="text.secondary">
                  {t("EvaluationDetail.noEvaluationContentMessage")}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Follow-up Questions */}
          {evaluation.follow_up_questions &&
            evaluation.follow_up_questions.length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t("EvaluationDetail.followUpQuestionsTitle")}
                  </Typography>
                  {evaluation.follow_up_questions.map(
                    (q: any, i: number) => (
                      <Box key={i} sx={{ mb: 2 }}>
                        <Typography variant="body1" fontWeight={500}>
                          {i + 1}. {q.question}
                        </Typography>
                        {q.why_asked && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 2 }}
                          >
                            <Trans
                              i18nKey="EvaluationDetail.followUpWhyLabel"
                              t={t}
                              values={{ why: q.why_asked }}
                              components={[<em key="em0" />]}
                            />
                          </Typography>
                        )}
                        {q.how_to_prepare && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 2 }}
                          >
                            <Trans
                              i18nKey="EvaluationDetail.followUpPrepareLabel"
                              t={t}
                              values={{ prepare: q.how_to_prepare }}
                              components={[<em key="em0" />]}
                            />
                          </Typography>
                        )}
                      </Box>
                    )
                  )}
                </CardContent>
              </Card>
            )}
        </Grid>
      </Grid>
      )}

      {/* Upgrade prompt when tier limit is reached */}
      <UpgradePrompt
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        currentUsage={tierLimitInfo.currentUsage}
        limit={tierLimitInfo.limit}
      />
    </Box>
  );
}
